<script lang="ts" setup>
  import { computed } from 'vue';
  import { useShared, useHttpClient, useRoute } from '@othercommerce/foundation';
  import type { WarehouseResource } from '@storefront/resources';

  interface Props {
    warehouses: WarehouseResource[];
  }

  defineProps<Props>();

  const open = defineModel('open', { type: Boolean, required: false, default: false });
  const route = useRoute();
  const http = useHttpClient();
  const shared = useShared();

  const selected = computed(() => {
    return shared.value.currentPickupPoint as WarehouseResource;
  });

  function selectWarehouse(warehouse: WarehouseResource) {
    http.patch(route('api.v1.web.warehouse.update'), { warehouse: warehouse.id }).then(() => {
      open.value = false;
    });
  }
</script>
<template>
  <UiDrawer md v-model:open="open" id="warehouses-drawer" class="warehouses-drawer" aria-labelledby="warehouses-drawer-title">
    <SfDrawerHeader v-model:open="open" headingId="warehouses-drawer-title">
      {{ $t('SHOP.COMMON.HEADER.CHOOSE_WAREHOUSE') }}
    </SfDrawerHeader>
    <div class="warehouses">
      <template v-if="warehouses?.length > 0">
        <SfListCard v-for="warehouse in warehouses" :key="warehouse.id" class="warehouse" :aria-labelledby="'warehouse-address-'+ warehouse.id" small interactive :selected="warehouse.id === selected.id" @click="selectWarehouse(warehouse)" @keydown.space="selectWarehouse(warehouse)">
          <template #default>
            <address class="warehouse-address" :id="'warehouse-address-'+ warehouse.id">
              <p v-if="warehouse.city" class="warehouse-address-name" v-text="warehouse.city" />
              <p v-if="warehouse.firstLine" class="warehouse-address-line" v-text="warehouse.firstLine" />
              <p v-if="warehouse.secondLine" class="warehouse-address-line" v-text="warehouse.secondLine" />
              <p v-if="warehouse.thirdLine" class="warehouse-address-line" v-text="warehouse.thirdLine" />
            </address>
          </template>
        </SfListCard>
      </template>
      <template v-else>
        <SfDataPlaceholder>
          {{ $t('SHOP.COMMON.MESSAGE.NO_WAREHOUSE_AVAILABLE') }}
        </SfDataPlaceholder>
      </template>
    </div>
  </UiDrawer>
</template>
<style scoped lang="scss">
  @use "@storefront/styles/mixins/media";

  .warehouses-drawer :deep( .drawer__content) {
    gap: 2rem;

    @include media.query(md) {
      gap: 1rem;
    }
  }

  .warehouses {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .warehouse-address {
    font-style: normal;
  }

  .warehouse-address-name {
    font-weight: var(--font-weight-bold);
  }

  .warehouse-address-line {
    font-size: 0.75rem;
  }
</style>
